import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, Spinner, CheckCircle, Circle, Play, X, Trash, ChatCircle,
  Robot, Megaphone, ShoppingCart, UsersThree, ChatText,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { TASK_ROLES } from "@/components/TaskAssignModal";
import { useAuth, hasRole, userRoles } from "@/context/AuthContext";

const SOURCE_META = {
  inventory: { label: "Inventario", icon: Robot, color: "text-[#002FA7]" },
  marketing: { label: "Marketing", icon: Megaphone, color: "text-pink-600" },
  purchasing: { label: "Compras", icon: ShoppingCart, color: "text-emerald-600" },
};

const STATUS_META = {
  pending_approval: { label: "Pendiente aprobación", color: "bg-zinc-100 text-zinc-600" },
  approved: { label: "Por hacer", color: "bg-blue-100 text-blue-700" },
  in_progress: { label: "En progreso", color: "bg-amber-100 text-amber-700" },
  done: { label: "Hecho", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rechazada", color: "bg-rose-100 text-rose-700" },
};

const ROLE_COLORS = TASK_ROLES.reduce((acc, r) => { acc[r.value] = r.color; return acc; }, {});

export default function MyTasks() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "super_admin");
  const myRoles = userRoles(user);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("active"); // active | all | done
  const [filterRole, setFilterRole] = useState(isAdmin ? "all" : "");
  const [filterSource, setFilterSource] = useState("");
  const [addNoteFor, setAddNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isAdmin) params.mine = true;
      else if (filterRole && filterRole !== "all") params.assigned_role = filterRole;
      if (filterSource) params.source_analysis_id = undefined;  // not used here
      const { data } = await api.get("/alendra/tasks", { params });
      setTasks(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron cargar las tareas");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterRole]);

  const updateTask = async (tid, patch) => {
    try {
      const { data } = await api.patch(`/alendra/tasks/${tid}`, patch);
      setTasks((prev) => prev.map((t) => t.id === tid ? data : t));
      toast.success("Tarea actualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const remove = async (tid) => {
    if (!confirm("¿Eliminar esta tarea?")) return;
    try {
      await api.delete(`/alendra/tasks/${tid}`);
      setTasks((prev) => prev.filter((t) => t.id !== tid));
      toast.success("Tarea eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const sendNote = async () => {
    if (!addNoteFor || !noteText.trim()) return;
    await updateTask(addNoteFor.id, { note: noteText.trim() });
    setNoteText("");
    setAddNoteFor(null);
  };

  // Aplicar filtros locales
  const filtered = tasks.filter((t) => {
    if (filterStatus === "active" && !["approved", "in_progress"].includes(t.status)) return false;
    if (filterStatus === "done" && t.status !== "done") return false;
    if (filterSource && t.source !== filterSource) return false;
    return true;
  });

  const counts = {
    active: tasks.filter(t => ["approved","in_progress"].includes(t.status)).length,
    done: tasks.filter(t => t.status === "done").length,
    all: tasks.length,
  };

  return (
    <div className="space-y-4" data-testid="my-tasks-page">
      <div>
        <h1 className="font-heading text-3xl font-bold inline-flex items-center gap-2">
          <Sparkle size={28} weight="fill" className="text-[#002FA7]"/>
          Mis Tareas
        </h1>
        <p className="text-sm text-zinc-500">
          {isAdmin
            ? "Vista de todas las tareas asignadas por Alendra AI a tu equipo."
            : `Tareas asignadas a tu rol (${myRoles.join(", ") || "—"}). Marca tu avance.`}
        </p>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5 text-xs">
          {[
            { v: "active", label: `Activas (${counts.active})` },
            { v: "done", label: `Hechas (${counts.done})` },
            { v: "all", label: `Todas (${counts.all})` },
          ].map((s) => (
            <button
              key={s.v}
              type="button"
              onClick={() => setFilterStatus(s.v)}
              data-testid={`filter-status-${s.v}`}
              className={`px-3 py-1.5 rounded font-semibold ${filterStatus === s.v ? "bg-[#002FA7] text-white" : "text-zinc-500 hover:text-zinc-800"}`}
            >{s.label}</button>
          ))}
        </div>
        {isAdmin && (
          <select
            value={filterRole}
            onChange={(e)=>setFilterRole(e.target.value)}
            data-testid="filter-role"
            className="text-xs border border-zinc-200 rounded px-2 py-1.5 bg-white"
          >
            <option value="all">Todos los equipos</option>
            {TASK_ROLES.map((r)=>(<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
        )}
        <select
          value={filterSource}
          onChange={(e)=>setFilterSource(e.target.value)}
          data-testid="filter-source"
          className="text-xs border border-zinc-200 rounded px-2 py-1.5 bg-white"
        >
          <option value="">Todos los análisis</option>
          <option value="inventory">📦 Inventario</option>
          <option value="marketing">📣 Marketing</option>
          <option value="purchasing">🛒 Compras</option>
        </select>
        <Button onClick={load} variant="outline" size="sm" data-testid="tasks-reload">Recargar</Button>
      </div>

      {/* Lista */}
      {loading && (
        <div className="border border-zinc-200 rounded-lg p-10 text-center bg-white" data-testid="tasks-loading">
          <Spinner size={20} className="animate-spin inline mr-2 text-[#002FA7]"/>
          <span className="text-sm text-zinc-500">Cargando tareas…</span>
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-zinc-200 rounded-lg p-10 text-center text-zinc-500" data-testid="tasks-empty">
          <CheckCircle size={48} weight="fill" className="inline mb-2 text-emerald-300"/>
          <p className="font-semibold">Sin tareas {filterStatus === "done" ? "completadas" : "pendientes"}</p>
          <p className="text-xs mt-1">Cuando el admin apruebe nuevas tareas, aparecerán aquí.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <ul className="space-y-2">
          {filtered.map((t) => {
            const SourceIcon = SOURCE_META[t.source]?.icon || Sparkle;
            const sourceColor = SOURCE_META[t.source]?.color || "text-zinc-500";
            const status = STATUS_META[t.status] || STATUS_META.approved;
            const isDone = t.status === "done";
            return (
              <li key={t.id} className={`border rounded-lg p-3 transition ${isDone ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-white hover:border-zinc-300"}`} data-testid={`task-${t.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center ${sourceColor}`}>
                    <SourceIcon size={18} weight="fill"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-bold text-sm ${isDone ? "line-through text-zinc-500" : "text-zinc-800"}`}>{t.title}</span>
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${ROLE_COLORS[t.assigned_role] || "bg-zinc-100 text-zinc-600"}`}>{TASK_ROLES.find(r => r.value === t.assigned_role)?.label || t.assigned_role}</span>
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${status.color}`}>{status.label}</span>
                      {t.priority && (
                        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${t.priority === "alta" ? "bg-rose-100 text-rose-700" : t.priority === "media" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600"}`}>{t.priority}</span>
                      )}
                    </div>
                    {t.description && <div className="text-xs text-zinc-600 mt-1 whitespace-pre-wrap">{t.description}</div>}
                    {t.hook && (
                      <div className="text-xs italic text-zinc-700 mt-1.5 bg-zinc-50 rounded px-2 py-1">🎬 &ldquo;{t.hook}&rdquo;</div>
                    )}
                    {(t.notes || []).length > 0 && (
                      <div className="mt-2 border-l-2 border-zinc-200 pl-2 space-y-1">
                        {t.notes.map((n, i) => (
                          <div key={i} className="text-[11px] text-zinc-600">
                            <span className="font-semibold">{n.by_name || "Usuario"}:</span> {n.text}
                            <span className="text-zinc-400 ml-1">· {new Date(n.at).toLocaleString("es-MX", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-[10px] text-zinc-400">
                      <span>Creada: {new Date(t.created_at).toLocaleString("es-MX", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</span>
                      {t.completed_at && <span>· Hecha: {new Date(t.completed_at).toLocaleString("es-MX", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {!isDone && t.status === "approved" && (
                      <button type="button" onClick={() => updateTask(t.id, { status: "in_progress" })} data-testid={`task-${t.id}-start`} className="px-2.5 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-bold uppercase inline-flex items-center gap-1">
                        <Play size={10} weight="fill"/> Empezar
                      </button>
                    )}
                    {!isDone && (
                      <button type="button" onClick={() => updateTask(t.id, { status: "done" })} data-testid={`task-${t.id}-done`} className="px-2.5 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-[10px] font-bold uppercase inline-flex items-center gap-1">
                        <CheckCircle size={10} weight="fill"/> Hecho
                      </button>
                    )}
                    {isDone && (
                      <button type="button" onClick={() => updateTask(t.id, { status: "approved" })} data-testid={`task-${t.id}-reopen`} className="px-2.5 py-1 rounded bg-white hover:bg-zinc-100 border border-zinc-200 text-zinc-600 text-[10px] font-bold uppercase inline-flex items-center gap-1">
                        <Circle size={10}/> Reabrir
                      </button>
                    )}
                    <button type="button" onClick={() => { setAddNoteFor(t); setNoteText(""); }} data-testid={`task-${t.id}-note`} className="px-2.5 py-1 rounded bg-white hover:bg-zinc-100 border border-zinc-200 text-zinc-600 text-[10px] font-bold uppercase inline-flex items-center gap-1">
                      <ChatText size={10} weight="bold"/> Nota
                    </button>
                    {isAdmin && (
                      <button type="button" onClick={() => remove(t.id)} data-testid={`task-${t.id}-delete`} className="px-2.5 py-1 rounded bg-white hover:bg-rose-50 border border-zinc-200 hover:border-rose-200 text-rose-600 text-[10px] font-bold uppercase inline-flex items-center gap-1">
                        <Trash size={10}/>
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {addNoteFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAddNoteFor(null)} data-testid="task-note-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
              <ChatText size={18} weight="fill" className="text-[#002FA7]"/>
              <h3 className="font-bold text-sm">Agregar nota a tarea</h3>
              <button type="button" onClick={() => setAddNoteFor(null)} className="ml-auto p-1 hover:bg-zinc-100 rounded"><X size={14}/></button>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-xs text-zinc-500">{addNoteFor.title}</div>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={3}
                data-testid="task-note-input"
                placeholder="Comparte un avance, una pregunta o un bloqueo…"
                className="w-full border border-zinc-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
              />
            </div>
            <div className="px-4 py-3 border-t border-zinc-200 flex justify-end gap-2">
              <button type="button" onClick={() => setAddNoteFor(null)} className="px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 rounded">Cancelar</button>
              <button type="button" onClick={sendNote} disabled={!noteText.trim()} data-testid="task-note-submit" className="px-3 py-1.5 text-xs bg-[#002FA7] hover:bg-[#002277] text-white rounded disabled:opacity-50">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
